// src/pages/user/SuccessStories.jsx
import React, { useState, useEffect } from 'react';
import {
  Table, Modal, Button, Input, Select, Tooltip,
  message, Tag, Avatar, Row, Col, Form, DatePicker, Empty
} from 'antd';
import {
  EyeOutlined, EditOutlined, DeleteOutlined, PlusOutlined,
  TrophyOutlined, HeartFilled, EnvironmentOutlined,
  CalendarOutlined, CloseOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ClockCircleOutlined,
  FileTextOutlined, CameraOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  User_StateGet,
  getDistrictsByState,
  getMySuccessStories,
  submitSuccessStory,
  updateMyStory,
  deleteMyStory
} from '../../../Api/CoreApi';

const { TextArea } = Input;
const { Option } = Select;

const SuccessStories = () => {

  const userRole = localStorage.getItem('role') || 'user';
  const isAdmin = ['admin', 'superadmin'].includes(userRole);
  const isStaff = userRole === 'staff';

  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('all');
  const [viewStory, setViewStory] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  const [statesList, setStatesList] = useState([]);
  const [districtsList, setDistrictsList] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const stats = {
    total: stories.length,
    pending: stories.filter(s => s.status === 'pending').length,
    approved: stories.filter(s => s.status === 'approved').length,
    rejected: stories.filter(s => s.status === 'rejected').length,
  };

  const displayStories = filter === 'all' ? stories : stories.filter(s => s.status === filter);

  useEffect(() => { fetchStories(); }, []);

  const fetchStories = async () => {
    setLoading(true);
    try {
      const data = await getMySuccessStories();
      setStories(Array.isArray(data) ? data : (data?.data || []));
    } catch { message.error('Failed to load stories'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const loadStates = async () => {
      try {
        const res = await User_StateGet();
        if (Array.isArray(res)) setStatesList(res);
        else if (res?.data) setStatesList(res.data);
      } catch {}
    };
    loadStates();
  }, []);

  const handleStateChange = async (value) => {
    form.setFieldsValue({ district: undefined });
    setDistrictsList([]);
    if (!value) return;
    setLoadingDistricts(true);
    try {
      const response = await getDistrictsByState(value);
      setDistrictsList(response?.districts || (Array.isArray(response) ? response : []));
    } catch { message.error('Failed to load districts'); }
    finally { setLoadingDistricts(false); }
  };

  const resetForm = () => {
    form.resetFields();
    setImageFile(null);
    setImagePreview(null);
    setEditingId(null);
    setDistrictsList([]);
  };

  const openAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (story) => {
    setEditingId(story.id);
    form.setFieldsValue({
      name: story.name || '',
      husband_name: story.husband_name || '',
      wife_name: story.wife_name || '',
      marriage_date: story.marriage_date ? dayjs(story.marriage_date) : null,
      text: story.text || '',
      district: story.district || undefined,
      state: story.state || undefined,
    });
    setImagePreview(story.img_url || story.img || null);
    setImageFile(null);
    if (story.state) handleStateChange(story.state);
    setViewStory(null);
    setShowForm(true);
  };

  // ✅ SIMPLE IMAGE UPLOAD HANDLER
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      message.error("Only JPG, PNG, WEBP allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error("Max 5MB allowed");
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (values) => {
    if (!values.husband_name?.trim()) return message.error("Husband's name required");
    if (!values.wife_name?.trim()) return message.error("Wife's name required");
    if (!values.text?.trim()) return message.error('Story required');

    setSubmitting(true);
    try {
      const formData = new FormData();
      const title = values.name?.trim() || `${values.husband_name.trim()} & ${values.wife_name.trim()}`;
      formData.append('name', title);
      formData.append('husband_name', values.husband_name);
      formData.append('wife_name', values.wife_name);
      formData.append('text', values.text);
      formData.append('district', values.district || '');
      formData.append('state', values.state || '');
      if (values.marriage_date) formData.append('marriage_date', values.marriage_date.format('YYYY-MM-DD'));
      if (imageFile) formData.append('img', imageFile, imageFile.name || 'couple.jpg');

      let result;
      if (editingId) result = await updateMyStory(editingId, formData);
      else result = await submitSuccessStory(formData);

      const ok = result && (result.success || result.id || result.data?.id || result.name);
      if (ok) {
        message.success(editingId ? '✅ Story updated!' : '✅ Story submitted for approval!');
        resetForm(); setShowForm(false); fetchStories();
      } else {
        message.error('Failed to submit');
      }
    } catch { message.error('Failed to save story'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete this story?',
      content: 'This action cannot be undone.',
      okText: 'Delete', okType: 'danger', cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteMyStory(id);
          message.success('Story deleted');
          setViewStory(null); fetchStories();
        } catch { message.error('Failed to delete'); }
      }
    });
  };

  const getImage = (s) => s?.img_url || s?.img || null;
  const getRefId = (id) => `SS${String(id).padStart(6, '0')}`;
  const getAvatar = (story) => (story.husband_name || story.name || 'C').charAt(0).toUpperCase();
  const getAvatarColor = (id) => ['#ff0055', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ec4899'][id % 6];

  const getStatusTag = (status) => {
    const config = {
      pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Pending' },
      approved: { color: 'success', icon: <CheckCircleOutlined />, text: 'Approved' },
      rejected: { color: 'error', icon: <CloseCircleOutlined />, text: 'Rejected' },
    };
    const c = config[status] || config.pending;
    return <Tag color={c.color} icon={c.icon} style={{ margin: 0, fontWeight: 600, borderRadius: 6, fontSize: 11 }}>{c.text}</Tag>;
  };

  // ✅ COMPACT TABLE COLUMNS
  const columns = [
    {
      title: '#', key: 'index', width: 45, align: 'center',
      render: (_, __, idx) => <span style={{ color: '#94a3b8', fontWeight: 700, fontSize: 12 }}>{idx + 1}</span>
    },
    {
      title: 'REF ID', key: 'ref', width: 105,
      render: (_, story) => (
        <span style={{
          background: '#fff0f3', color: '#ff0055',
          padding: '4px 9px', borderRadius: 6,
          fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3,
          whiteSpace: 'nowrap', display: 'inline-block'
        }}>
          {getRefId(story.id)}
        </span>
      )
    },
    {
      title: 'PHOTO', key: 'photo', width: 70, align: 'center',
      render: (_, story) => (
        getImage(story) ? (
          <img
            src={getImage(story)}
            alt=""
            style={{
              width: 44, height: 44,
              borderRadius: 8, objectFit: 'cover',
              objectPosition: 'top', border: '2px solid #fff',
              boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
            }}
          />
        ) : (
          <Avatar size={44}
            style={{
              background: getAvatarColor(story.id),
              fontWeight: 700, fontSize: 15
            }}>
            {getAvatar(story)}
          </Avatar>
        )
      )
    },
    {
      title: 'COUPLE', key: 'couple', width: 220,
      render: (_, story) => (
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: '#0f172a',
            display: 'flex', alignItems: 'center', gap: 4
          }}>
            <span style={{ color: '#3b82f6', fontSize: 10 }}>♂</span>
            {story.husband_name || '—'}
          </div>
          <div style={{
            fontSize: 12, fontWeight: 600, color: '#ff0055',
            marginTop: 3, display: 'flex', alignItems: 'center', gap: 4
          }}>
            <HeartFilled style={{ fontSize: 9, color: '#ff0055' }} />
            {story.wife_name || '—'}
          </div>
        </div>
      )
    },
    {
      title: 'DATE', key: 'date', width: 90,
      render: (_, story) => {
        if (!story.marriage_date) return <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>;
        const d = new Date(story.marriage_date);
        return (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
              {d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
            </div>
            <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>{d.getFullYear()}</div>
          </div>
        );
      }
    },
    {
      title: 'LOCATION', key: 'location', width: 140,
      render: (_, story) => {
        const loc = [story.district, story.state].filter(Boolean).join(', ');
        if (!loc) return <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b' }}>
            <EnvironmentOutlined style={{ color: '#ff0055' }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {loc}
            </span>
          </div>
        );
      }
    },
    {
      title: 'STATUS', key: 'status', width: 105, align: 'center',
      render: (_, story) => getStatusTag(story.status)
    },
    {
      title: 'ACTIONS', key: 'actions', width: 130, fixed: 'right', align: 'center',
      render: (_, story) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
          <Tooltip title="View">
            <Button
              type="text" icon={<EyeOutlined />}
              onClick={() => setViewStory(story)}
              style={{
                background: '#fef2f4', color: '#ff0055',
                width: 30, height: 30, borderRadius: 8, padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text" icon={<EditOutlined />}
              onClick={() => openEdit(story)}
              style={{
                background: '#fef3c7', color: '#d97706',
                width: 30, height: 30, borderRadius: 8, padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text" icon={<DeleteOutlined />}
              onClick={() => handleDelete(story.id)}
              style={{
                background: '#fee2e2', color: '#dc2626',
                width: 30, height: 30, borderRadius: 8, padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            />
          </Tooltip>
        </div>
      )
    },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');
        * { font-family: 'Poppins', sans-serif !important; }

        .ss-page { background: #f8fafc; min-height: 100vh; padding: 24px 20px; }
        .ss-wrap { max-width: 1250px; margin: 0 auto; }

        /* HEADER */
        .ss-header-card { background: #fff; padding: 16px 22px; border-radius: 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); border: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 14px; margin-bottom: 16px; }
        .ss-title { font-size: 20px; font-weight: 800; color: #0f172a; display: flex; align-items: center; gap: 10px; margin: 0; }

        /* STATS */
        .ss-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px; }
        .ss-stat-card { padding: 14px 16px; border-radius: 12px; border: 1px solid; cursor: pointer; transition: all .2s; background: #fff; display: flex; align-items: center; justify-content: space-between; }
        .ss-stat-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.06); }

        /* FILTERS */
        .ss-filters { display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap; }

        /* COMPACT TABLE */
        .ss-antd-table .ant-table { background: #fff !important; border-radius: 12px !important; overflow: hidden; }
        .ss-antd-table .ant-table-thead > tr > th { background: #fafbfc !important; font-size: 10.5px !important; font-weight: 700 !important; color: #64748b !important; text-transform: uppercase; letter-spacing: 0.6px; padding: 12px 12px !important; border-bottom: 1px solid #e2e8f0 !important; }
        .ss-antd-table .ant-table-thead > tr > th::before { display: none !important; }
        .ss-antd-table .ant-table-tbody > tr > td { padding: 10px 12px !important; border-bottom: 1px solid #f1f5f9 !important; vertical-align: middle; }
        .ss-antd-table .ant-table-tbody > tr:hover > td { background: #fef8fa !important; }
        .ss-antd-table .ant-table-cell-fix-right { background: #fff !important; }
        .ss-antd-table .ant-table-tbody > tr:hover .ant-table-cell-fix-right { background: #fef8fa !important; }
        .ss-antd-table .ant-pagination { padding: 14px 20px !important; margin: 0 !important; background: #fafbfc; border-top: 1px solid #e2e8f0; }

        /* ========================================================================
           ✅ PREMIUM MODAL DESIGN
           ======================================================================== */
        .ss-premium-modal .ant-modal-content {
          padding: 0 !important;
          border-radius: 20px !important;
          overflow: hidden;
          box-shadow: 0 25px 60px rgba(255, 0, 85, 0.15) !important;
        }

        .ss-premium-modal .ant-modal-body {
          padding: 0 !important;
        }

        /* Premium Header with Gradient */
        .ss-pm-header {
          background: linear-gradient(135deg, #ff0055 0%, #ff4d88 50%, #ff6b9d 100%);
          padding: 22px 26px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
          overflow: hidden;
        }

        .ss-pm-header::before {
          content: '';
          position: absolute;
          top: -50%; right: -20%;
          width: 200px; height: 200px;
          background: radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%);
          border-radius: 50%;
        }

        .ss-pm-header-left {
          display: flex;
          align-items: center;
          gap: 14px;
          z-index: 1;
        }

        .ss-pm-header-icon {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          color: #fff;
          border: 1.5px solid rgba(255, 255, 255, 0.35);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .ss-pm-header-text h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 800;
          color: #fff;
          line-height: 1.2;
        }

        .ss-pm-header-text p {
          margin: 3px 0 0;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 500;
        }

        .ss-pm-close {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          border: 1.5px solid rgba(255, 255, 255, 0.3);
          color: #fff;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.25s ease;
          z-index: 1;
        }
        .ss-pm-close:hover {
          background: rgba(255, 255, 255, 0.35);
          transform: rotate(90deg);
        }

        /* Body */
        .ss-pm-body {
          padding: 24px;
          max-height: 70vh;
          overflow-y: auto;
          background: #fafbfc;
        }
        .ss-pm-body::-webkit-scrollbar { width: 6px; }
        .ss-pm-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 6px; }
        .ss-pm-body::-webkit-scrollbar-thumb:hover { background: #ff80aa; }

        /* Section Cards */
        .ss-pm-section {
          background: #fff;
          padding: 16px 18px;
          border-radius: 14px;
          border: 1px solid #f0f0f0;
          margin-bottom: 14px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.02);
        }

        .ss-pm-section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 800;
          color: #ff0055;
          margin-bottom: 14px;
          padding-bottom: 10px;
          border-bottom: 1.5px dashed #ffe4ec;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .ss-pm-section-title .anticon {
          font-size: 14px;
        }

        /* Form Labels */
        .ss-pm-body .ant-form-item-label > label {
          font-size: 12px !important;
          font-weight: 700 !important;
          color: #334155 !important;
          height: auto !important;
        }

        .ss-pm-body .ant-form-item-required::before {
          color: #ff0055 !important;
        }

        /* ✅ FIX: Clean Inputs - No double borders */
        .ss-pm-body .ant-input,
        .ss-pm-body .ant-picker,
        .ss-pm-body .ant-select-selector {
          border-radius: 10px !important;
          background: #f8fafc !important;
          border: 1.5px solid #e2e8f0 !important;
          transition: all .25s ease !important;
          font-size: 13px !important;
          height: 42px !important;
        }

        .ss-pm-body .ant-select-selector {
          display: flex !important;
          align-items: center !important;
          padding: 0 12px !important;
        }

        .ss-pm-body .ant-select-selection-item,
        .ss-pm-body .ant-select-selection-placeholder {
          line-height: 40px !important;
          font-size: 13px !important;
        }

        .ss-pm-body .ant-picker-input > input {
          font-size: 13px !important;
        }

        .ss-pm-body .ant-input:hover,
        .ss-pm-body .ant-picker:hover,
        .ss-pm-body .ant-select:hover .ant-select-selector {
          border-color: #ffb3c6 !important;
          background: #fff !important;
        }

        .ss-pm-body .ant-input:focus,
        .ss-pm-body .ant-input-focused,
        .ss-pm-body .ant-picker-focused,
        .ss-pm-body .ant-select-focused .ant-select-selector {
          border-color: #ff0055 !important;
          background: #fff !important;
          box-shadow: 0 0 0 3px rgba(255, 0, 85, 0.08) !important;
        }

        /* Textarea */
        .ss-pm-body .ant-input[data-count] + textarea,
        .ss-pm-body textarea.ant-input {
          height: auto !important;
          min-height: 100px !important;
        }

        /* ========================================================================
           ✅ IMAGE UPLOAD - FIXED (No Overlap)
           ======================================================================== */
        .ss-pm-image-section {
          background: #fff;
          padding: 16px 18px;
          border-radius: 14px;
          border: 1px solid #f0f0f0;
          margin-bottom: 14px;
        }

        .ss-pm-upload-box {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 30px 20px;
          background: linear-gradient(135deg, #fff0f3, #ffe4ec);
          border: 2.5px dashed #ff80aa;
          border-radius: 14px;
          cursor: pointer;
          transition: all 0.3s ease;
          min-height: 180px;
        }
        .ss-pm-upload-box:hover {
          border-color: #ff0055;
          background: linear-gradient(135deg, #ffe4ec, #ffd6e7);
          transform: translateY(-2px);
        }
        .ss-pm-upload-box input[type="file"] {
          display: none;
        }

        .ss-pm-upload-icon {
          font-size: 40px;
          color: #ff0055;
          margin-bottom: 10px;
        }

        .ss-pm-upload-text {
          font-size: 14px;
          font-weight: 700;
          color: #ff0055;
          margin-bottom: 4px;
        }

        .ss-pm-upload-hint {
          font-size: 11px;
          color: #999;
        }

        /* Preview Container */
        .ss-pm-preview-wrap {
          position: relative;
          width: 100%;
          border-radius: 14px;
          overflow: hidden;
          border: 3px solid #ff0055;
          background: #000;
          box-shadow: 0 4px 12px rgba(255, 0, 85, 0.2);
        }

        .ss-pm-preview-wrap img {
          width: 100%;
          height: 250px;
          object-fit: contain;
          object-position: center;
          display: block;
          background: #f8fafc;
        }

        .ss-pm-preview-remove {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 36px;
          height: 36px;
          background: rgba(220, 38, 38, 0.95);
          color: #fff;
          border: none;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          transition: all 0.2s;
          z-index: 2;
        }
        .ss-pm-preview-remove:hover {
          background: #b91c1c;
          transform: scale(1.1);
        }

        .ss-pm-change-photo {
          display: flex;
          justify-content: center;
          margin-top: 12px;
        }

        .ss-pm-change-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 18px;
          background: #fff0f3;
          color: #ff0055;
          border: 1.5px solid #ff0055;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
        }
        .ss-pm-change-btn:hover {
          background: #ff0055;
          color: #fff;
        }

        .ss-pm-change-btn input[type="file"] {
          display: none;
        }

        /* Submit Button */
        .ss-pm-submit-wrap {
          padding-top: 8px;
          border-top: 1.5px dashed #ffe4ec;
          margin-top: 6px;
        }

        .ss-pm-submit-btn {
          background: linear-gradient(135deg, #ff0055, #ff4d88) !important;
          border: none !important;
          height: 48px !important;
          border-radius: 12px !important;
          font-weight: 700 !important;
          font-size: 14px !important;
          box-shadow: 0 6px 18px rgba(255, 0, 85, 0.3) !important;
          transition: all 0.25s ease !important;
        }
        .ss-pm-submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 22px rgba(255, 0, 85, 0.4) !important;
        }

        /* View Modal Image */
        .ss-view-img { width: 100%; max-height: 280px; object-fit: contain; background: #0f172a; border-radius: 12px; margin-bottom: 16px; }
        .ss-view-img-ph { width: 100%; height: 180px; background: #f8fafc; border-radius: 12px; border: 2px dashed #cbd5e1; display: flex; align-items: center; justify-content: center; font-size: 50px; margin-bottom: 16px; }
        .ss-view-text { font-size: 13px; color: #334155; line-height: 1.75; padding: 16px; background: #fff0f3; border-left: 3px solid #ff0055; border-radius: 0 10px 10px 0; white-space: pre-wrap; }

        /* View Modal (Old Style) */
        .ss-modal .ant-modal-content { padding: 0 !important; border-radius: 16px !important; overflow: hidden; }
        .ss-modal .ant-modal-header { padding: 18px 22px !important; margin: 0 !important; border-bottom: 1px solid #f1f5f9 !important; border-radius: 16px 16px 0 0 !important; }
        .ss-modal .ant-modal-title { font-size: 16px !important; font-weight: 700 !important; color: #0f172a !important; }
        .ss-modal .ant-modal-body { padding: 20px !important; max-height: 70vh; overflow-y: auto; }
        .ss-modal .ant-modal-close { top: 12px !important; right: 12px !important; }
        .ss-modal .ant-modal-close-x { 
          background: #ff0055 !important; color: #fff !important; 
          width: 32px !important; height: 32px !important; 
          border-radius: 50% !important; line-height: 32px !important; 
          font-size: 14px !important; display: flex; align-items: center; justify-content: center;
        }

        @media (max-width: 768px) {
          .ss-page { padding: 14px 10px; }
          .ss-stats { grid-template-columns: 1fr 1fr; }
          .ss-pm-header { padding: 16px 18px; }
          .ss-pm-body { padding: 16px; }
          .ss-pm-header-text h3 { font-size: 16px; }
          .ss-pm-header-text p { font-size: 11px; }
        }
      `}</style>

      <div className="ss-page">
        <div className="ss-wrap">

          {/* HEADER */}
          <div className="ss-header-card">
            <h1 className="ss-title">
              <TrophyOutlined style={{ color: '#ff0055' }} />
              Success Stories
            </h1>
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}
              style={{ background: 'linear-gradient(135deg, #ff0055, #ff4d88)', border: 'none', height: 40, borderRadius: 10, fontWeight: 700, boxShadow: '0 3px 12px rgba(255,0,85,.25)' }}
            >
              Add Story
            </Button>
          </div>

          {/* STATS */}
          <div className="ss-stats">
            {[
              { key: 'all', label: 'TOTAL', num: stats.total, bg: '#f8fafc', border: '#e2e8f0', color: '#0f172a', icon: <HeartFilled /> },
              { key: 'pending', label: 'PENDING', num: stats.pending, bg: '#fff7ed', border: '#fed7aa', color: '#ea580c', icon: <ClockCircleOutlined /> },
              { key: 'approved', label: 'APPROVED', num: stats.approved, bg: '#f0fdf4', border: '#bbf7d0', color: '#16a34a', icon: <CheckCircleOutlined /> },
              { key: 'rejected', label: 'REJECTED', num: stats.rejected, bg: '#fef2f2', border: '#fecaca', color: '#dc2626', icon: <CloseCircleOutlined /> },
            ].map((s) => (
              <div key={s.key} className="ss-stat-card"
                style={{ background: s.bg, borderColor: filter === s.key ? s.color : s.border }}
                onClick={() => setFilter(s.key)}
              >
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: s.color, letterSpacing: 0.5 }}>{s.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: s.color, marginTop: 4, lineHeight: 1 }}>{s.num}</div>
                </div>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: `${s.color}20`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: s.color, fontSize: 18
                }}>{s.icon}</div>
              </div>
            ))}
          </div>

          {/* FILTERS */}
          <div className="ss-filters">
            {[
              { key: 'all', label: `All (${stats.total})` },
              { key: 'pending', label: `⏳ Pending (${stats.pending})` },
              { key: 'approved', label: `✅ Approved (${stats.approved})` },
              { key: 'rejected', label: `❌ Rejected (${stats.rejected})` },
            ].map((f) => (
              <Button key={f.key}
                onClick={() => setFilter(f.key)}
                type={filter === f.key ? 'primary' : 'default'}
                style={{
                  borderRadius: 8, height: 36, fontWeight: 600, fontSize: 12,
                  background: filter === f.key ? 'linear-gradient(135deg, #ff0055, #ff4d88)' : '#fff',
                  border: filter === f.key ? 'none' : '1px solid #e2e8f0',
                  color: filter === f.key ? '#fff' : '#64748b'
                }}
              >
                {f.label}
              </Button>
            ))}
          </div>

          <Table
            className="ss-antd-table"
            columns={columns}
            dataSource={displayStories}
            rowKey="id"
            loading={loading}
            scroll={{ x: 950 }}
            size="middle"
            pagination={{
              pageSize: 10,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} stories`,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
            }}
            locale={{
              emptyText: (
                <Empty
                  image={<div style={{ fontSize: 50 }}>💝</div>}
                  imageStyle={{ height: 70 }}
                  description={
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
                        {filter !== 'all' ? `No ${filter} stories` : "You haven't shared any story"}
                      </div>
                      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
                        Share your journey to inspire thousands!
                      </div>
                      {filter === 'all' && (
                        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}
                          style={{ background: 'linear-gradient(135deg, #ff0055, #ff4d88)', border: 'none', borderRadius: 8, fontWeight: 600 }}>
                          Create Story
                        </Button>
                      )}
                    </div>
                  }
                />
              )
            }}
          />
        </div>
      </div>

      {/* ===== ✅ PREMIUM ADD/EDIT MODAL ===== */}
      <Modal
        className="ss-premium-modal"
        open={showForm}
        onCancel={() => { setShowForm(false); resetForm(); }}
        footer={null}
        width={700}
        centered
        destroyOnClose
        closable={false}
      >
        {/* Premium Header */}
        <div className="ss-pm-header">
          <div className="ss-pm-header-left">
            <div className="ss-pm-header-icon">
              {editingId ? <EditOutlined /> : <HeartFilled />}
            </div>
            <div className="ss-pm-header-text">
              <h3>{editingId ? 'Edit Success Story' : 'Share Success Story'}</h3>
              <p>{editingId ? 'Update the couple details' : 'Add a beautiful love story'}</p>
            </div>
          </div>
          <button className="ss-pm-close" onClick={() => { setShowForm(false); resetForm(); }}>
            <CloseOutlined />
          </button>
        </div>

        {/* Body */}
        <div className="ss-pm-body">
          <Form form={form} layout="vertical" onFinish={handleSubmit}>

            {/* IMAGE SECTION */}
            <div className="ss-pm-image-section">
              <div className="ss-pm-section-title">
                <CameraOutlined /> Couple Photo (Optional)
              </div>

              {imagePreview ? (
                <>
                  <div className="ss-pm-preview-wrap">
                    <img src={imagePreview} alt="Preview" />
                    <button
                      type="button"
                      className="ss-pm-preview-remove"
                      onClick={removeImage}
                      title="Remove photo"
                    >
                      <CloseOutlined />
                    </button>
                  </div>
                  <div className="ss-pm-change-photo">
                    <label className="ss-pm-change-btn">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,image/webp"
                        onChange={handleImageChange}
                      />
                      <CameraOutlined /> Change Photo
                    </label>
                  </div>
                </>
              ) : (
                <label className="ss-pm-upload-box">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,image/webp"
                    onChange={handleImageChange}
                  />
                  <CameraOutlined className="ss-pm-upload-icon" />
                  <div className="ss-pm-upload-text">Click to Upload Photo</div>
                  <div className="ss-pm-upload-hint">JPG, PNG, WEBP • Max 5MB</div>
                </label>
              )}
            </div>

            {/* Story Details Section */}
            <div className="ss-pm-section">
              <div className="ss-pm-section-title">
                <FileTextOutlined /> Story Details
              </div>

              <Form.Item name="name" label="Story Title (Optional)" style={{ marginBottom: 14 }}>
                <Input placeholder="e.g. A Match Made in Heaven" />
              </Form.Item>

              <Row gutter={14}>
                <Col span={12}>
                  <Form.Item name="husband_name" label="Husband's Name" rules={[{ required: true, message: 'Required' }]} style={{ marginBottom: 0 }}>
                    <Input placeholder="Full name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="wife_name" label="Wife's Name" rules={[{ required: true, message: 'Required' }]} style={{ marginBottom: 0 }}>
                    <Input placeholder="Full name" />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Date & Location Section (City removed) */}
            <div className="ss-pm-section">
              <div className="ss-pm-section-title">
                <EnvironmentOutlined /> Date & Location
              </div>

              <Row gutter={14}>
                <Col span={12}>
                  <Form.Item name="marriage_date" label="Marriage Date" style={{ marginBottom: 14 }}>
                    <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" placeholder="Select date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="state" label="State" style={{ marginBottom: 14 }}>
                    <Select placeholder="Select State" showSearch allowClear
                      onChange={handleStateChange}
                      filterOption={(input, option) => (option?.children || '').toLowerCase().includes(input.toLowerCase())}>
                      {statesList.map((s, idx) => {
                        const name = typeof s === 'string' ? s : (s.state || s.name);
                        return <Option key={idx} value={name}>{name}</Option>;
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* ✅ Only District (City removed) */}
              <Form.Item name="district" label="District" style={{ marginBottom: 0 }}>
                <Select placeholder={loadingDistricts ? "Loading..." : "Select District"}
                  showSearch allowClear disabled={loadingDistricts || districtsList.length === 0}
                  loading={loadingDistricts}
                  filterOption={(input, option) => (option?.children || '').toLowerCase().includes(input.toLowerCase())}>
                  {districtsList.map((d, idx) => {
                    const name = typeof d === 'string' ? d : (d.name || d.district);
                    return <Option key={idx} value={name}>{name}</Option>;
                  })}
                </Select>
              </Form.Item>
            </div>

            {/* Their Story Section */}
            <div className="ss-pm-section">
              <div className="ss-pm-section-title">
                <HeartFilled /> Their Love Story
              </div>

              <Form.Item name="text" rules={[{ required: true, message: 'Story required' }]} style={{ marginBottom: 0 }}>
                <TextArea rows={5} placeholder="Tell us how you met, your journey together, and what made this match special..." showCount maxLength={2000} />
              </Form.Item>
            </div>

            {/* Submit */}
            <div className="ss-pm-submit-wrap">
              <Button type="primary" htmlType="submit" loading={submitting} block
                icon={editingId ? <EditOutlined /> : <HeartFilled />}
                className="ss-pm-submit-btn"
              >
                {editingId ? 'Update Story' : 'Submit Story for Approval'}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      {/* ===== VIEW MODAL ===== */}
      <Modal
        className="ss-modal"
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HeartFilled style={{ color: '#ff0055' }} />
            Story Details
          </div>
        }
        open={!!viewStory}
        onCancel={() => setViewStory(null)}
        footer={null}
        width={550}
        centered
        closeIcon={<CloseOutlined style={{ color: '#fff', fontSize: 12 }} />}
      >
        {viewStory && (
          <div>
            {getImage(viewStory) ? (
              <img src={getImage(viewStory)} alt="couple" className="ss-view-img" />
            ) : (
              <div className="ss-view-img-ph">💑</div>
            )}

            <div style={{ textAlign: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
                {viewStory.husband_name || '—'}
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0' }}>
                <HeartFilled style={{ color: '#ff0055' }} />
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#ff0055' }}>
                {viewStory.wife_name || '—'}
              </div>
            </div>

            {viewStory.name && (
              <p style={{ color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', fontSize: 13, marginBottom: 12 }}>
                "{viewStory.name}"
              </p>
            )}

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
              {getStatusTag(viewStory.status)}
            </div>

            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', padding: '10px 12px', background: '#f8fafc', borderRadius: 10, marginBottom: 14 }}>
              {viewStory.marriage_date && (
                <span style={{ fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 500 }}>
                  <CalendarOutlined style={{ color: '#ff0055' }} />
                  {new Date(viewStory.marriage_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                </span>
              )}
              {(viewStory.district || viewStory.state) && (
                <span style={{ fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 500 }}>
                  <EnvironmentOutlined style={{ color: '#ff0055' }} />
                  {[viewStory.district, viewStory.state].filter(Boolean).join(', ')}
                </span>
              )}
            </div>

            <div className="ss-view-text">{viewStory.text}</div>

            {viewStory.status === 'rejected' && viewStory.admin_note && (
              <div style={{ marginTop: 14, padding: '10px 14px', background: '#fef2f2', borderRadius: 10, border: '1px solid #fecaca', color: '#b91c1c', fontSize: 13 }}>
                <strong>❌ Reason:</strong> {viewStory.admin_note}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <Button icon={<EditOutlined />} block onClick={() => openEdit(viewStory)}
                style={{ background: '#fef3c7', color: '#d97706', border: 'none', height: 40, borderRadius: 8, fontWeight: 600 }}>
                Edit
              </Button>
              <Button icon={<DeleteOutlined />} danger block onClick={() => handleDelete(viewStory.id)}
                style={{ height: 40, borderRadius: 8, fontWeight: 600 }}>
                Delete
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default SuccessStories;